/**
 * Dispute Service Layer
 *
 * Implements business logic for DevStory 4.5: Sistema de resolución de disputas
 * con participación de la DAO.
 */

import {
  apiCreateDispute,
  apiGetDispute,
  apiGetDisputesByMilestone,
  apiAddDisputeArgument,
  apiGetDisputeArguments,
  apiGetDisputeHistory,
  apiGetDisputeMessages,
  apiSendDisputeMessage,
  apiResolveDisputeDAO,
} from '@/api/adapter';

import type {
  Dispute,
  DisputeArgument,
  DisputeArgumentType,
  DisputeHistory,
  DisputeMessage,
} from '@/types';

/**
 * Open a new dispute for a milestone that has been rejected.
 *
 * Criterio 1-6:
 * - Rechazo previo obligatorio.
 * - Argumentación obligatoria de apertura.
 * - Registro automático del expediente e histórico del proyecto/hito.
 */
export async function openDispute(
  projectId: string,
  milestoneId: string,
  reason: string
): Promise<Dispute> {
  const trimmedReason = reason.trim();
  if (!trimmedReason) {
    throw new Error('Dispute opening reason is mandatory');
  }

  return apiCreateDispute(projectId, milestoneId, trimmedReason);
}

/**
 * Get dispute details.
 */
export async function getDispute(disputeId: string): Promise<Dispute> {
  return apiGetDispute(disputeId);
}

/**
 * Get disputes associated with a milestone.
 */
export async function getDisputesForMilestone(
  projectId: string,
  milestoneId: string
): Promise<Dispute[]> {
  return apiGetDisputesByMilestone(projectId, milestoneId);
}

/**
 * Add an argument or response to a dispute.
 *
 * Criterio 7 & 8:
 * - La parte contraria o el iniciador pueden incorporar argumentos.
 * - Inmutabilidad y trazabilidad garantizadas.
 */
export async function addDisputeArgument(
  disputeId: string,
  content: string,
  argumentType: DisputeArgumentType
): Promise<DisputeArgument> {
  const trimmed = content.trim();
  if (!trimmed) {
    throw new Error('Argument content cannot be empty');
  }

  return apiAddDisputeArgument(disputeId, trimmed, argumentType);
}

/**
 * Get all arguments associated with a dispute.
 */
export async function getDisputeArguments(
  disputeId: string
): Promise<DisputeArgument[]> {
  return apiGetDisputeArguments(disputeId);
}

/**
 * Get full dispute history, deliverables, rejections, and domain events.
 *
 * Criterio 6 & 12:
 * - Contexto estructurado para evaluación de la DAO.
 */
export async function getDisputeHistory(
  disputeId: string
): Promise<DisputeHistory> {
  return apiGetDisputeHistory(disputeId);
}

/**
 * Get messages from the communication channel linked to the dispute.
 *
 * Criterio 9:
 * - Canal de comunicación creado automáticamente para tratar el conflicto.
 */
export async function getDisputeMessages(
  disputeId: string
) {
  return apiGetDisputeMessages(disputeId);
}

/**
 * Send a message in the dispute communication channel.
 */
export async function sendDisputeMessage(
  disputeId: string,
  content: string
): Promise<DisputeMessage> {
  const trimmed = content.trim();
  if (!trimmed) {
    throw new Error('Message cannot be empty');
  }

  return apiSendDisputeMessage(disputeId, trimmed);
}

/**
 * Resolve dispute through DAO governance or arbitrator.
 *
 * Criterio 12:
 * - Fallo a favor (RESOLVED) o en contra (REJECTED) de quien abrió la disputa.
 */
export async function resolveDisputeDAO(
  disputeId: string,
  outcome: 'RESOLVED' | 'REJECTED',
  reason: string,
  notes?: string,
  token?: string,
  winner?: 'client' | 'professional',
  winnerName?: string,
  documentation?: string,
  otherLinks?: string
): Promise<Dispute> {
  const trimmedReason = reason.trim();
  if (!trimmedReason) {
    throw new Error('Resolution reason is mandatory');
  }

  return apiResolveDisputeDAO(
    disputeId,
    outcome,
    trimmedReason,
    notes,
    token,
    winner,
    winnerName,
    documentation,
    otherLinks
  );
}
